<template>
  <div class="header-image-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2><i class="el-icon-picture-outline"></i> 官网图片管理</h2>
      <p class="page-desc">管理官网顶部轮播展示的5张图片，替换后的图片将实时生效</p>
    </div>

    <!-- 图片列表 -->
    <div class="image-grid">
      <div
        v-for="item in imageList"
        :key="item.key"
        class="image-card"
      >
        <div class="card-header">
          <span class="card-title">{{ item.label }}</span>
        </div>
        <div class="card-body">
          <div class="image-preview" @click="handlePreview(item)">
            <img
              :src="item.url"
              :alt="item.label"
              @error="onImageError($event, item)"
            />
            <div class="image-overlay">
              <i class="el-icon-zoom-in"></i>
              <span>点击预览</span>
            </div>
          </div>
          <div class="image-info">
            <p class="file-name">文件名: {{ item.fileName }}</p>
            <p class="oss-path">OSS路径: {{ item.ossPath }}{{ item.fileName }}</p>
          </div>
        </div>
        <div class="card-footer">
          <el-button
            type="primary"
            icon="el-icon-edit"
            size="medium"
            @click="handleEdit(item)"
          >
            编辑替换
          </el-button>
        </div>
      </div>
    </div>

    <!-- 隐藏的文件选择框 -->
    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
      style="display: none"
      @change="onFileSelected"
    />

    <!-- 图片预览弹窗 -->
    <el-dialog
      :visible.sync="previewVisible"
      :title="previewItem ? previewItem.label : '图片预览'"
      width="600px"
      top="5vh"
      :close-on-click-modal="true"
    >
      <div class="preview-container">
        <img
          v-if="previewItem"
          :src="previewItem.url"
          :alt="previewItem.label"
          class="preview-image"
          @error="onPreviewImageError"
        />
      </div>
    </el-dialog>

    <!-- 上传确认弹窗 -->
    <el-dialog
      :visible.sync="confirmVisible"
      title="确认替换图片"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="confirm-content" v-if="selectedItem && selectedFile">
        <p>即将替换 <strong>{{ selectedItem.label }}</strong> 的图片：</p>
        <div class="compare-images">
          <div class="compare-item">
            <p class="compare-label">原图片：</p>
            <img :src="selectedItem.url" class="compare-img" @error="onImageError($event, selectedItem)" />
          </div>
          <div class="compare-arrow">
            <i class="el-icon-arrow-right"></i>
          </div>
          <div class="compare-item">
            <p class="compare-label">新图片：</p>
            <img :src="selectedFilePreview" class="compare-img" />
          </div>
        </div>
        <p class="file-info">新文件: {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})</p>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmVisible = false" :disabled="uploading">取 消</el-button>
        <el-button
          type="primary"
          :loading="uploading"
          @click="handleUpload"
        >
          {{ uploading ? '上传中...' : '确认上传' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 上传进度弹窗 -->
    <el-dialog
      :visible.sync="progressVisible"
      title="正在上传"
      width="400px"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <div class="progress-content">
        <p class="progress-label" v-if="selectedItem">
          正在上传 <strong>{{ selectedItem.label }}</strong> ...
        </p>
        <div class="progress-bar-wrapper">
          <el-progress
            :percentage="uploadProgress"
            :status="progressStatus"
            :stroke-width="16"
            :text-inside="true"
          ></el-progress>
        </div>
        <p class="progress-tip" v-if="uploadProgress < 100">请稍候，文件上传中...</p>
        <p class="progress-tip success-tip" v-if="uploadProgress === 100 && progressStatus === 'success'">上传完成！正在刷新页面...</p>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { HEADER_IMAGE_CONFIG, getImageUrl, uploadImageToOSS } from '@/utils/ossUpload'

export default {
  name: 'HeaderImageManagement',
  data() {
    return {
      // 图片列表数据
      imageList: [],
      // 当前正在操作的图片项
      selectedItem: null,
      // 当前选中的文件
      selectedFile: null,
      // 选中的文件预览URL
      selectedFilePreview: '',
      // 上传中标志
      uploading: false,
      // 预览弹窗
      previewVisible: false,
      previewItem: null,
      // 确认弹窗
      confirmVisible: false,
      // 进度弹窗
      progressVisible: false,
      // 上传进度 0-100
      uploadProgress: 0,
      // 进度状态: '' / 'success' / 'exception'
      progressStatus: ''
    }
  },
  created() {
    this.loadImageList()
  },
  methods: {
    /**
     * 加载图片列表
     */
    loadImageList() {
      this.imageList = HEADER_IMAGE_CONFIG.map(item => ({
        key: item.key,
        label: item.label,
        fileName: item.fileName,
        ossPath: item.ossPath,
        url: getImageUrl(item.key)
      }))
    },

    /**
     * 处理图片加载失败
     */
    onImageError(event, item) {
      event.target.src = '' // 清除无效的src
      event.target.alt = '图片加载失败'
      event.target.style.objectFit = 'none'
      event.target.style.padding = '20px'
    },

    /**
     * 点击预览图片
     */
    handlePreview(item) {
      this.previewItem = item
      this.previewVisible = true
    },

    /**
     * 预览图片加载失败处理
     */
    onPreviewImageError() {
      this.$message.warning('原图片加载失败，请确认OSS配置是否正确')
    },

    /**
     * 点击编辑按钮 - 弹出文件选择框
     */
    handleEdit(item) {
      this.selectedItem = item
      // 触发隐藏的文件选择框
      this.$refs.fileInput.value = '' // 重置，确保能选中同一个文件再次触发
      this.$refs.fileInput.click()
    },

    /**
     * 用户选择了文件后的回调
     */
    onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return

      // 检查文件类型
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('请选择 PNG、JPG、GIF 或 WebP 格式的图片')
        return
      }

      // 检查文件大小（限制10MB）
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 10MB')
        return
      }

      this.selectedFile = file
      // 生成预览URL
      this.selectedFilePreview = URL.createObjectURL(file)
      // 显示确认弹窗
      this.confirmVisible = true
    },

    /**
     * 确认上传
     */
    async handleUpload() {
      if (!this.selectedItem || !this.selectedFile) return

      // 关闭确认弹窗，打开进度弹窗
      this.confirmVisible = false
      this.progressVisible = true
      this.uploadProgress = 0
      this.progressStatus = ''
      this.uploading = true

      try {
        const url = await uploadImageToOSS(
          this.selectedItem.key,
          this.selectedFile,
          // 进度回调函数
          (percent) => {
            this.uploadProgress = percent
          }
        )
        // 进度到100%
        this.uploadProgress = 100
        this.progressStatus = 'success'

        // 更新列表中的URL（带时间戳防缓存）
        const item = this.imageList.find(i => i.key === this.selectedItem.key)
        if (item) {
          item.url = url
        }

        // 延迟1秒后关闭进度弹窗并提示成功
        setTimeout(() => {
          this.progressVisible = false
          this.$message.success(`${this.selectedItem.label} 图片替换成功！`)
        }, 1000)
      } catch (error) {
        this.progressStatus = 'exception'
        const errorMsg = (error && error.message) ? error.message : '图片上传失败，请检查网络及OSS配置后重试'
        // 延迟关闭进度弹窗，显示错误状态
        setTimeout(() => {
          this.progressVisible = false
          this.$message.error(errorMsg)
        }, 500)
        console.error('上传异常详情:', error)
      } finally {
        this.uploading = false
        // 释放预览URL
        if (this.selectedFilePreview) {
          URL.revokeObjectURL(this.selectedFilePreview)
          this.selectedFilePreview = ''
        }
        this.selectedFile = null
      }
    },

    /**
     * 格式化文件大小
     */
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.header-image-management {
  padding: 20px;
}

/* 页面标题 */
.page-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
  color: #333;
}

.page-header h2 i {
  margin-right: 8px;
  color: #1890ff;
}

.page-header .page-desc {
  margin: 0;
  font-size: 14px;
  color: #999;
}

/* 图片网格 */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

/* 图片卡片 */
.image-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
}

.image-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-header {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.card-body {
  padding: 16px;
  flex: 1;
}

/* 图片预览区 */
.image-preview {
  position: relative;
  width: 100%;
  height: 180px;
  background: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e8e8e8;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-overlay i {
  font-size: 32px;
  margin-bottom: 8px;
}

.image-overlay span {
  font-size: 14px;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

/* 图片信息 */
.image-info {
  margin-top: 12px;
}

.image-info p {
  margin: 4px 0;
  font-size: 12px;
  color: #999;
}

.image-info .file-name {
  color: #666;
}

/* 卡片底部 */
.card-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  text-align: center;
}

/* 预览弹窗 */
.preview-container {
  text-align: center;
  max-height: 70vh;
  overflow: auto;
}

.preview-image {
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
}

/* 确认弹窗 */
.confirm-content {
  padding: 10px 0;
}

.confirm-content p {
  margin: 0 0 16px 0;
  font-size: 14px;
}

.compare-images {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
}

.compare-item {
  flex: 1;
  text-align: center;
}

.compare-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px !important;
}

.compare-img {
  width: 100%;
  max-height: 150px;
  object-fit: contain;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fafafa;
}

.compare-arrow {
  font-size: 28px;
  color: #1890ff;
  flex-shrink: 0;
}

.file-info {
  color: #999 !important;
  font-size: 13px !important;
  text-align: center;
}

/* 进度弹窗样式 */
.progress-content {
  padding: 10px 0;
  text-align: center;
}

.progress-label {
  font-size: 15px;
  color: #333;
  margin-bottom: 20px !important;
}

.progress-bar-wrapper {
  padding: 0 10px;
  margin-bottom: 16px;
}

.progress-tip {
  font-size: 13px;
  color: #999;
  margin: 0 !important;
}

.progress-tip.success-tip {
  color: #67c23a;
}
</style>
